import { useAnalyticsService } from '@sec-loans-ui/state/analyticsContext'
import { onFormSubmitCheckDependantData } from '@sec-loans-ui/utils/dependantChanges'
import { assertValue } from '@shared/helpers'
import { SecLoansAbortModalCase } from '@shared/sec-loans'
import { AppId } from '@shared/types'
import { CommonWizardWrapper } from '@ui-common/components/CommonWizardWrapper'
import { OpenModalEvent } from '@ui-common/components/Modal'
import { getCancelWithoutSavingModalEvent } from '@ui-common/components/Modal/CancelWithoutSavingModal'
import { GlobalServicesContext } from '@ui-common/state'
import { commonExit } from '@ui-common/utils/helpers'
import { getLanguage } from '@ui-common/utils/language'
import { ModalVisualMode } from '@ui-components/containers/ModalTypes'
import { BackActionTopBar } from '@ui-components/navigation/BackActionTopBar'
import { BodyText } from '@ui-components/typography/Typography'
import { useSelector } from '@xstate/react'
import { JSX, lazy, use, useCallback, useEffect, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'

import { SecLoansAnalyticsService, SecLoansCustomEventCategory } from '../services/analyticsService'
import { ApplicationWizardContext } from '../state/applicationWizardContext'
import { getSecLoansLocalizedEnums } from '../utils/getSecLoansLocalizedEnums'
import { getExitUrl, getFallbackExitUrls } from '../utils/helpers'
import { ProgressFormSendType, ProgressFormType, WizardData } from './types'
import {
  PHASE_APPLICANT_INFO,
  PHASE_LOAN_INFO,
  PHASE_TARGET_INFO,
  STEP_ABORTED,
  STEP_CANCEL_APPLICATION,
  STEP_CATALOG,
  STEP_GET_ALL_APPLICATIONS,
  STEP_GET_APPLICATION,
  STEP_RETURN_APPLICATION,
  STEP_SEND,
  STEP_STATUS,
  STEP_SUCCEEDED,
  STEP_THE_END,
} from './wizardMachine'

const ApplicantInfoWizard = lazy(() => import('./steps/applicant-info-phase/ApplicantInfoWizard'))
const CancelApplicationStep = lazy(() => import('./steps/cancel-application-step/CancelApplicationStep'))
const CatalogStep = lazy(() => import('./steps/catalog-step/CatalogStep'))
const GetAllApplicationsStep = lazy(() => import('./steps/get-all-applications-step/GetAllApplicationsStep'))
const GetApplicationStep = lazy(() => import('./steps/get-application-step/GetApplicationStep'))
const LoanInfoWizard = lazy(() => import('./steps/loan-info-phase/LoanInfoWizard'))
const TargetInfoWizard = lazy(() => import('./steps/loan-target-phase/TargetInfoWizard'))
const ReturnApplicationStep = lazy(() => import('./steps/return-to-primary-applicant-step/ReturnApplicationStep'))
const SendStep = lazy(() => import('./steps/status-send-step/SendStep'))
const StatusStep = lazy(() => import('./steps/status-step/StatusStep'))
const TheEndStep = lazy(() => import('./steps/the-end-step/TheEndStep'))

export const getSecLoansOpenAbortModalEvent = ({
  onAbort,
  intl,
  analyticsService,
  analyticsCategory,
  abortCase = SecLoansAbortModalCase.LeaveAndContinueLater,
}: {
  onAbort: () => void
  intl: IntlShape
  analyticsService: SecLoansAnalyticsService
  analyticsCategory: SecLoansCustomEventCategory
  abortCase?: SecLoansAbortModalCase
}): OpenModalEvent => {
  const { abortModalHeaderTexts, abortModalDescriptionTexts } = getSecLoansLocalizedEnums(intl.formatMessage)
  return {
    type: 'OPEN',
    data: {
      header: abortModalHeaderTexts[abortCase],
      children: (
        <BodyText key={`sec-loans-abort-${abortCase}prompt-message`} tagName="div">
          {abortModalDescriptionTexts[abortCase]}
        </BodyText>
      ),
      primaryButtonOptions: {
        label: intl.formatMessage({ id: 'common-cancel' }),
        onClick: () => {
          analyticsService.sendCustomEvent(analyticsCategory, 'CancelButton')
        },
      },
      secondaryButtonOptions: {
        label: intl.formatMessage({ id: 'common-abort-prompt-exit' }),
        onClick: onAbort,
      },
      mode: ModalVisualMode.Help,
    },
  }
}

export const Wizard = (): JSX.Element => {
  const { service } = use(ApplicationWizardContext)
  const {
    value: step,
    context: {
      data: wizardData,
      exitUrls: contextExitUrls,
      stepToEdit,
      user,
      applicationMetadata,
      validationParams,
      authToken,
      catalogStepData,
    },
  } = useSelector(service, (s) => s)
  const isEditing = !!stepToEdit

  const { analyticsService } = useAnalyticsService()

  const { modalService } = use(GlobalServicesContext)

  const onAbort = () => {
    service.send({ type: 'EXIT_WIZARD' })
  }

  const modalSend = modalService.send

  const intl = useIntl()

  const language = getLanguage()

  const [isFormDirty, setIsFormDirty] = useState(false)

  const onFormStepSubmit: ProgressFormType = (changedData, stepSend) => {
    const onSubmit = (type: ProgressFormSendType, data: WizardData) =>
      stepSend({
        type,
        data,
      })

    if (isEditing) {
      onFormSubmitCheckDependantData({
        wizardData,
        changedData,
        applicationMetadata: assertValue(applicationMetadata, 'applicationMetadata'),
        validationParams: assertValue(validationParams, 'validationParams'),
        submit: (data) => onSubmit('SUBMIT', data),
        modalSend,
        children: (
          <BodyText tagName="div">{intl.formatMessage({ id: 'sec-loans-dependant-changes-modal-message' })}</BodyText>
        ),
        intl,
      })
    } else {
      onSubmit('SUBMIT', changedData)
    }
  }

  useEffect(() => {
    void (async () => {
      if (step === STEP_ABORTED || step === STEP_SUCCEEDED) {
        const exitUrls = contextExitUrls ?? getFallbackExitUrls()
        const baseUrl = step === STEP_SUCCEEDED ? exitUrls.successUrl[language] : exitUrls.failureUrl[language]
        const url = await getExitUrl(baseUrl, exitUrls.cbsTokenParameter)
        await commonExit(url)
      }
    })()
  }, [language, step, contextExitUrls])

  const showAbortPromptModal = () => {
    analyticsService.sendModalViewEvent('AbortModal')
    const openEvent: OpenModalEvent = getSecLoansOpenAbortModalEvent({
      onAbort: () => {
        analyticsService.sendCustomEvent('AbortModal', 'ExitButton')
        onAbort()
      },
      analyticsService,
      analyticsCategory: 'AbortModal',
      intl,
    })
    modalSend(openEvent)
  }

  const showSendAbortPromptModal = () => {
    analyticsService.sendModalViewEvent('SendAbortModal')
    const openEvent: OpenModalEvent = getSecLoansOpenAbortModalEvent({
      onAbort: () => {
        analyticsService.sendCustomEvent('SendAbortModal', 'ExitButton')
        onAbort()
      },
      analyticsService,
      analyticsCategory: 'SendAbortModal',
      intl,
      abortCase: SecLoansAbortModalCase.TiredOfWaiting,
    })
    modalSend(openEvent)
  }

  const showDiscardChangesModal = () => {
    analyticsService.sendModalViewEvent('DiscardChangesModal')
    const continueAction = intl.formatMessage({ id: 'common-continue-anyway' })

    modalSend(
      getCancelWithoutSavingModalEvent({
        onConfirm: () => {
          analyticsService.sendCustomEvent('DiscardChangesModal', 'ConfirmButton')
          onBackToApplication()
        },
        onCancel: () => {
          analyticsService.sendCustomEvent('DiscardChangesModal', 'CancelButton')
        },
        intl,
        header: intl.formatMessage({ id: 'common-back-to-application-unsaved-header' }),
        text: intl.formatMessage(
          { id: 'common-back-to-application-unsaved-warning' },
          { continue_action: continueAction },
        ),
        primaryActionText: continueAction,
      }),
    )
  }

  const onBackToApplication = () => {
    service.send({ type: 'BACK_TO_STATUS' })
  }

  const onWizardSectionFinished = () => service.send({ type: 'SECTION_DONE' })

  const sectionWizardProps = {
    showAbortPromptModal,
    onAbort,
    onFinished: onWizardSectionFinished,
    onFormStepSubmit,
  }

  const onFormIsDirtyChangeCallback = useCallback((isDirty: boolean) => {
    setIsFormDirty(isDirty)
  }, [])

  const isInPhase = step === PHASE_TARGET_INFO || step === PHASE_APPLICANT_INFO || step === PHASE_LOAN_INFO

  const stepComponentMap: Record<string, () => React.ReactNode> = {
    [STEP_GET_ALL_APPLICATIONS]: () => (
      <GetAllApplicationsStep parentService={service} onAbort={showAbortPromptModal} />
    ),
    [STEP_CATALOG]: () => <CatalogStep parentService={service} showAbortPromptModal={showAbortPromptModal} />,
    [STEP_GET_APPLICATION]: () => (
      <GetApplicationStep
        parentService={service}
        applicationId={catalogStepData?.applicationId}
        onAbort={showAbortPromptModal}
      />
    ),
    [STEP_STATUS]: () => (
      <StatusStep
        parentService={service}
        showAbortPromptModal={showAbortPromptModal}
        user={assertValue(user, 'user')}
      />
    ),
    [PHASE_TARGET_INFO]: () => (
      <TargetInfoWizard {...sectionWizardProps} onFormIsDirtyChange={onFormIsDirtyChangeCallback} />
    ),
    [PHASE_APPLICANT_INFO]: () => (
      <ApplicantInfoWizard {...sectionWizardProps} onFormIsDirtyChange={onFormIsDirtyChangeCallback} />
    ),
    [PHASE_LOAN_INFO]: () => (
      <LoanInfoWizard {...sectionWizardProps} onFormIsDirtyChange={onFormIsDirtyChangeCallback} />
    ),
    [STEP_SEND]: () => (
      <SendStep
        parentService={service}
        onAbort={showSendAbortPromptModal}
        applicationId={assertValue(catalogStepData?.applicationId, 'catalogStepData.applicationId')}
      />
    ),
    [STEP_CANCEL_APPLICATION]: () => (
      <CancelApplicationStep
        parentService={service}
        applicationId={assertValue(catalogStepData?.applicationId, 'catalogStepData.applicationId')}
        onAbort={showAbortPromptModal}
      />
    ),
    [STEP_RETURN_APPLICATION]: () => (
      <ReturnApplicationStep
        parentService={service}
        applicationId={assertValue(catalogStepData?.applicationId, 'catalogStepData.applicationId')}
        onAbort={showAbortPromptModal}
      />
    ),
    [STEP_THE_END]: () => <TheEndStep parentService={service} />,
  }

  return (
    <CommonWizardWrapper
      stepComponentMap={stepComponentMap}
      authToken={authToken ?? null}
      step={step}
      appId={AppId.SecLoans}
      analyticsService={analyticsService}
      onLogout={onAbort}
      userContext={null}
    >
      {isInPhase && !isEditing && (
        <BackActionTopBar
          backLabel={intl.formatMessage({ id: 'sec-loans-back-to-application-summary' })}
          onBackClick={() => {
            if (isFormDirty) {
              showDiscardChangesModal()
            } else {
              onBackToApplication()
            }
          }}
        />
      )}
    </CommonWizardWrapper>
  )
}
